import React, { useEffect, useRef } from 'react';
import Highcharts from 'highcharts/highstock';

const StockChart = ({ chartData }) => {
    const chartRef = useRef(null);

    useEffect(() => {
        if (!chartData) return;
        let station_data = chartData.historic;
        station_data.historic_data.sort((a, b) => new Date(a.date) - new Date(b.date));

        // Get min and max flow from `historic_data`
        const flowValues = station_data.historic_data.map(item => item.flow ?? 0);
        const minFlow = Math.min(...flowValues, 0);
        const maxFlow = Math.max(...flowValues);

        // Ensure return_periods are sorted
        station_data.return_periods.sort((a, b) => a.flow - b.flow);

        const returnPeriodData = station_data.return_periods
            .filter(period => period.flow >= minFlow && period.flow <= maxFlow)
            .reduce((acc, item, index, filteredArray) => {
                const previousFlow = index === 0 ? 0 : acc[acc.length - 1]?.to ?? 0;
                if (item.flow !== null && item.flow > previousFlow) {
                    acc.push({
                        from: previousFlow,
                        to: item.flow,
                        color: item.color
                    });
                }
                return acc;
            }, []);

        // Ensure the last band extends to `maxFlow` and has a unique color
        if (returnPeriodData.length > 0) {
            const lastTo = returnPeriodData[returnPeriodData.length - 1].to;
            const nextColor = station_data.return_periods.find(p => p.flow > lastTo)?.color || "#defaultColor";

            if (lastTo < maxFlow) {
                returnPeriodData.push({
                    from: lastTo,
                    to: maxFlow,
                    color: nextColor
                });
            }
        }

        const xAxisType = 'datetime';
        const xAxisFormatter = function () {
            return Highcharts.dateFormat('%b %d, %Y', this.value);
        };

        const chart = Highcharts.stockChart(chartRef.current, {
            chart: {
                type: 'spline',
                height: 450,
                scrollablePlotArea: {
                    minWidth: 600,
                    scrollPositionX: 1
                }
            },
            rangeSelector: {
                selected: 4, // Default to 1 year
                buttons: [
                    { type: 'week', count: 1, text: '1w' },
                    { type: 'month', count: 1, text: '1m' },
                    { type: 'month', count: 3, text: '3m' },
                    { type: 'month', count: 6, text: '6m' },
                    { type: 'year', count: 1, text: '1y' },
                    { type: 'all', text: 'All' }
                ]
            },
            title: {
                text: 'Flood category',
                align: 'left',
                style: {
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#105586'
                }
            },
            xAxis: {
                type: xAxisType,
                title: { text: 'Date' },
                labels: { formatter: xAxisFormatter }
            },
            yAxis: {
                title: { text: 'Flow (m³/s)' },
                min: minFlow,
                max: maxFlow,
                startOnTick: false,
                endOnTick: false,
                softMin: minFlow - 5,
                softMax: maxFlow + 5,
                labels: {
                    useHTML: true,
                    style: {
                        fontWeight: 'bold',
                        background: 'rgba(255, 255, 255, 0.7)',
                        padding: '2px',
                        borderRadius: '3px'
                    }
                },
                gridLineWidth: 0,
                plotBands: returnPeriodData
            },
            tooltip: {
                shared: true,
                useHTML: true,
                formatter: function () {
                    const formattedX = Highcharts.dateFormat('%b %d, %Y', this.x);
                    return `<b>${formattedX}</b><br>
                            <span style="color:${this.series.color}">●</span> 
                            ${this.series.name}: <b>${this.y.toFixed(2)} m³/s</b>`;
                },
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderColor: '#ccc',
                borderRadius: 5,
                shadow: true,
                style: {
                    whiteSpace: 'nowrap', // Prevent wrapping of tooltip content
                    maxWidth: '300px',    // Adjust max width of tooltip to prevent overflow
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'  // Add ellipsis if text is too long
                }
            },
            series: [{
                name: 'Historic Flow',
                data: station_data.historic_data.map(item => [new Date(item.date).getTime(), item.flow]),
                color: '#3125be',
                boostThreshold: 0, // Disable boost to ensure all points render
                turboThreshold: 0, // Disable threshold for large datasets
                dataGrouping: {
                    enabled: false // Disable data grouping to show all data points
                }
            }],
            exporting: { enabled: false },
            navigation: { menuItemStyle: { fontSize: '10px' } },
            credits: { enabled: false }
        });

        // Add visibility: hidden as inline style to elements with visibility="hidden" attribute
        const addInlineVisibility = () => {
            const hiddenElements = chartRef.current.querySelectorAll('[visibility="hidden"]');
            hiddenElements.forEach(element => {
                element.style.visibility = 'hidden';
            });
        };

        // Run after chart renders
        setTimeout(addInlineVisibility, 0); // Use setTimeout to ensure DOM is updated

        return () => {
            chart.destroy();
        };
    }, [chartData]);

    return <div ref={chartRef} style={{ height: '452px', width: '100%' }} />;
};

export default StockChart;